import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Table, Input, Button, Modal, message, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import 'antd/dist/reset.css';
import './TimeAndPayTable.css';
import BatchInfo from './BatchInfo';
import useAuth from '../../../hooks/useAuth';
import { exportBatchToKronos, getBatch, getBranches, saveBatch, updateBatch } from '../../../services/selfOnboarding';
import { timeInputWidths, payInputWidths, renderCell, getTimeColumns, getPayColumns, formatDateToYYYY } from './CellConfigAndHelpers';
import { isValidShortDate, isValidTime } from './CellConfigAndHelpers';
import { getDetails } from '../../../services/auth';

const POLL_INTERVAL = 5000; // 5 seconds

const TimeAndPayTable = () => {
  const makeRow = (index) => ({
    key: index,
    employeeId: '',
    name: '',
    inDate: null,
    outDate: null,
    inTime: '',
    outTime: '',
    wbs_io: '',
    task: '',
    sub_task: '',
    payscale: '',
    group: '',
    level: '',
    pa: '',
    costCenter: '',
    workRule: '',
    payCode: '',
    amount: '',
  });

  const [rows, setRows] = useState(Array.from({ length: 10 }, (_, i) => makeRow(i + 1)));
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [activeTab, setActiveTab] = useState('time');
  const timeRef = useRef(null);
  const payRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [employeeCount, setEmployeeCount] = useState(rows.length);
  const [batchId, setBatchId] = useState('');
  const [batchSuggestions, setBatchSuggestions] = useState([]);
  const { userConfig } = useAuth();
  const [focusedCell, setFocusedCell] = useState({ row: null, col: null });
  const inputRefs = useRef({}); // {rowKey: {colKey: ref}}
  const [isDirty, setIsDirty] = useState(false);
  const [originalBatchId, setOriginalBatchId] = useState('');
  const [isSentToKronos, setIsSentToKronos] = useState(false);
  // Compulsory fields for each tab
  const requiredFieldsTime = ['employeeId', 'name', 'inDate', 'inTime'];
  const requiredFieldsPay = ['employeeId', 'name', 'inDate', 'payCode', 'amount'];
  const requiredFields = activeTab === 'time' ? requiredFieldsTime : requiredFieldsPay;
  const [permissions, setPermissions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(undefined);

  const getZoomPermission = (tab) => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return tab === 'time' ? permissions.find((p) => p.feature === 'Time Entry') : permissions.find((p) => p.feature === 'PayCode Entry');
  };

  const zoomPerm = getZoomPermission(activeTab);
  const canCreate = !!(zoomPerm && zoomPerm?.access);
  const canEdit = !!(zoomPerm && zoomPerm?.access && zoomPerm?.edit && zoomPerm?.edit !== 'None');
  const canDelete = !!(zoomPerm && zoomPerm?.access && zoomPerm?.delete && zoomPerm?.delete !== 'None');

  const columnKeys = useMemo(() => {
    const cols = activeTab === 'time' ? Object.keys(timeInputWidths) : Object.keys(payInputWidths);
    return cols;
  }, [activeTab]);

  const focusCell = (rowIdx, colIdx) => {
    const rowKey = rows[rowIdx]?.key;
    const colKey = columnKeys[colIdx];
    if (inputRefs.current[rowKey] && inputRefs.current[rowKey][colKey]) {
      inputRefs.current[rowKey][colKey].focus();
      setFocusedCell({ row: rowIdx, col: colIdx });
    }
  };

  const cellRendererFactory = (field, type) =>
    renderCell({
      field,
      type,
      rows,
      columnKeys,
      inputRefs,
      setRows,
      setIsDirty,
      setFocusedCell,
      focusCell,
      selectedBranch,
    });

  // Now use this in your columns:
  const timeColumns = useMemo(() => getTimeColumns(cellRendererFactory, 'time'), [rows, columnKeys, selectedBranch]);

  const payColumns = useMemo(() => getPayColumns(cellRendererFactory, 'pay'), [rows, columnKeys, selectedBranch]);

  const anyInvalidEmployeeId = rows.some((r) => r.employeeIdInvalid);
  const anyInvalidWbsIo = rows.some((r) => r.wbsIoInvalid);
  const anyInvalidDate = rows.some((r) => (r.inDate && !isValidShortDate(r.inDate)) || (r.outDate && !isValidShortDate(r.outDate)));

  const anyInvalidTime = rows.some((r) => (r.inTime && !isValidTime(r.inTime)) || (r.outTime && !isValidTime(r.outTime)));
  const anyInvalidRow = anyInvalidEmployeeId || anyInvalidWbsIo || anyInvalidDate || anyInvalidTime;

  const allRowsSuccess =
    rows.filter((r) => Object.entries(r).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined)).length > 0 &&
    rows
      .filter((r) => Object.entries(r).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined))
      .every((r) => r.transactionStatus === 'success');

  const resetCellContent = (tab) => {
    setActiveTab(tab);
    setSelectedRowKeys([]);
    setRows(Array.from({ length: 10 }, (_, i) => makeRow(i + 1)));
    setEmployeeCount(0);
    setBatchId('');
    setBatchSuggestions([]);
    stopPollingStatus();
    setIsSentToKronos(false);
    setOriginalBatchId('');
  };

  const addRows = () => {
    setRows((prevRows) => {
      const nextKey = prevRows.length ? prevRows[prevRows.length - 1].key + 1 : 1;
      const newRows = Array.from({ length: 5 }, (_, i) => makeRow(nextKey + i));
      return [...prevRows, ...newRows];
    });
  };

  const handleTabChange = (tab) => {
    if (isDirty) {
      const nonEmptyRows = rows.filter((row) => Object.entries(row).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined));
      // Compulsory field validation
      const missingRequired = nonEmptyRows.some((row) => requiredFields.some((field) => !row[field] || row[field].toString().trim() === ''));

      // Invalid date/time validation
      const anyInvalidDate = nonEmptyRows.some((r) => (r.inDate && !isValidShortDate(r.inDate)) || (r.outDate && !isValidShortDate(r.outDate)));
      const anyInvalidTime = nonEmptyRows.some((r) => (r.inTime && !isValidTime(r.inTime)) || (r.outTime && !isValidTime(r.outTime)));

      Modal.confirm({
        title: 'Do you want to save the changes?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          try {
            // Check if user has access to current tab
            if (!zoomPerm?.access) {
              message.error('You do not have permission to save changes for this tab');
              setIsDirty(false);
              resetCellContent(tab);
              return;
            }
            if (!batchId) {
              message.error('Please enter a Batch ID before saving');
              return;
            }
            // permission checks: create vs edit
            const isUpdate = batchId === originalBatchId;
            // if (isUpdate && !canEdit) {
            //   message.error('You do not have permission to edit batches for this tab');
            //   return;
            // }
            // if (!isUpdate && !canCreate) {
            //   message.error('You do not have permission to create batches for this tab');
            //   return;
            // }
            // if (batchId === originalBatchId && isSentToKronos) {
            //   message.error('This batch has already been sent to Kronos. Changes cannot be saved with the same batch ID.');
            //   return;
            // }

            // Save or update batch
            const punchList = rows
              .filter((r) => Object.entries(r).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined))
              .map((row) => {
                const { key, nameLocked, employeeIdInvalid, wbsIoInvalid, ...rest } = row;
                // Omit _id only if saving a new batch
                if (batchId !== originalBatchId) {
                  const { _id, ...restWithoutId } = rest;
                  return {
                    ...restWithoutId,
                    inDate: formatDateToYYYY(rest.inDate),
                    outDate: formatDateToYYYY(rest.outDate),
                    amount: rest.amount?.toString().replace(/^\$/, ''), // Remove $ prefix
                    unit: rest.unit, // <-- Store the unit
                  };
                }
                // For update, keep _id
                return {
                  ...rest,
                  inDate: formatDateToYYYY(rest.inDate),
                  outDate: formatDateToYYYY(rest.outDate),
                  amount: rest.amount?.toString().replace(/^\$/, ''), // Remove $ prefix
                };
              });

            if (punchList.length === 0) {
              message.error('No entries to save');
              return;
            }

            if (missingRequired) {
              if (activeTab === 'time') {
                message.error('For Time Entry, please fill all compulsory fields: Employee ID, Name, In Date, and In Time.');
              } else if (activeTab === 'pay') {
                message.error('For Pay Code Edit, please fill all compulsory fields: Employee ID, Name, In Date, Pay Code, and Amount.');
              } else {
                message.error('Please fill all compulsory fields before switching tabs.');
              }
              return;
            }

            if (anyInvalidDate) {
              message.error('Please enter valid dates in MM-DD-YY format (month ≤ 12, day ≤ 31) before switching tabs.');
              return;
            }

            if (anyInvalidTime) {
              message.error('Please enter valid times in HH:MM format (hours ≤ 23, minutes ≤ 59) before switching tabs.');
              return;
            }

            const payload = {
              batchId: batchId,
              batchType: activeTab, // current tab type
              punchList,
              user: userConfig?.userid,
            };
            let response;
            if (batchId === originalBatchId) {
              response = await updateBatch({ ...payload, batchUpdates: {} });
              if (response?.data?.punchList && Array.isArray(response.data.punchList)) {
                const returnedPunchList = response.data.punchList;
                setRows((prevRows) =>
                  prevRows.map((row, index) => {
                    // Use the index to map the _id from the response punchList
                    const updatedRow = returnedPunchList[index];
                    return updatedRow ? { ...row, _id: updatedRow._id } : row;
                  }),
                );
              }
              message.success('Batch updated successfully!');
            } else {
              response = await saveBatch(payload);
              // --- Update rows with _id from response ---
              if (response?.data?.punchList && Array.isArray(response.data.punchList)) {
                const returnedPunchList = response.data.punchList;
                setRows((prevRows) =>
                  prevRows.map((row, index) => {
                    // Use the index to map the _id from the response punchList
                    const updatedRow = returnedPunchList[index];
                    return updatedRow ? { ...row, _id: updatedRow._id } : row;
                  }),
                );
              }
              message.success('Batch saved successfully!');
              setOriginalBatchId(batchId); // mark this as original
            }
            setIsDirty(false);
            setActiveTab(tab); // switch after save
            resetCellContent(tab);
          } catch (err) {
            console.error(err);
            message.error(err?.response?.data?.message || 'Error saving batch');
          }
        },
        onCancel: () => {
          // Discard changes and just switch
          setIsDirty(false);
          resetCellContent(tab);
        },
      });
    } else {
      setIsDirty(false);
      resetCellContent(tab);
    }
    // setActiveTab(tab);
  };

  const deleteSelected = () => {
    // if (!canDelete) {
    //   message.error('You do not have permission to delete rows in this tab');
    //   return;
    // }
    setRows((prev) => prev.filter((r) => !selectedRowKeys.includes(r.key)));
    setSelectedRowKeys([]); // Clear all selections after delete
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (record) => ({
      disabled: record.transactionStatus === 'success',
    }),
  };

  const generateAuditReport = () => {
    const punchList = rows
      .filter((r) => Object.entries(r).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined))
      .map(({ key, nameLocked, employeeIdInvalid, wbsIoInvalid, ...rest }) => rest);

    // Count unique employeeIds for the report
    const uniqueEmployeeIds = new Set(punchList.map((r) => r.employeeId && r.employeeId.toString().trim()).filter((id) => !!id));
    const employeeCount = uniqueEmployeeIds.size;
    const batchID = batchId; // example ID
    const user = userConfig?.firstName + ' ' + userConfig?.lastName; // replace with actual logged-in user
    // Add branch info here
    const branch = selectedBranch || '';
    // Create a new worksheet
    const wb = XLSX.utils.book_new();
    // Section 1: Batch Info
    const batchInfo = [
      ['Batch ID', batchID],
      ['User', user],
      ['Branch', branch],
      ['Employee Count', employeeCount],
    ];
    const batchSheet = XLSX.utils.aoa_to_sheet(batchInfo);
    // Add a blank row before table
    XLSX.utils.sheet_add_aoa(batchSheet, [[]], { origin: -1 });
    // Section 2: Table Data (with headers)
    const columns = activeTab === 'time' ? timeColumns : payColumns;
    const headers = columns.map((col) => col.exportTitle || (typeof col.title === 'string' ? col.title : col.dataIndex || '')).filter(Boolean);
    const data = punchList.map((row) => columns.map((col) => row[col.dataIndex]));
    XLSX.utils.sheet_add_aoa(batchSheet, [headers, ...data], { origin: -1 });
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, batchSheet, 'Audit Report');
    // Export file
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${batchID}-AuditReport.xlsx`);
  };

  const pollRef = useRef(null);

  // Helper to start polling
  const startPollingStatus = (batchId) => {
    console.log('Starting polling for batch:', batchId);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        // Call your API to get transaction status for batchId
        const { data } = await getBatch(batchId, false);
        const statusMap = data.batch?.submitToKronosStatus || {};
        setRows((prev) =>
          prev.map((row) => {
            const employeeStatus = statusMap[row.employeeId];
            if (employeeStatus) {
              const matchingPunch = employeeStatus.find((punch) => punch.punchId === row._id);
              if (matchingPunch) {
                return {
                  ...row,
                  transactionStatus: matchingPunch.status,
                  transactionError: matchingPunch.lastError || null,
                };
              }
            }
            return row;
          }),
        );

        // If no records in processing, stop polling
        const anyProcessing = Object.values(statusMap).some((statuses) => statuses.some((status) => status.status === 'processing'));
        if (!anyProcessing) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        console.error('Error polling transaction status', err);
      }
    }, POLL_INTERVAL);
  };

  // Stop polling
  const stopPollingStatus = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
      console.log('Stopped polling for batch', batchId);
    }
  };

  // Start polling after submit to Kronos
  const submitToKronos = async () => {
    // Check if batch is saved
    if (batchId !== originalBatchId || isDirty) {
      message.error('Please save the batch before submitting to Kronos');
      return;
    }
    
    const filteredRows = rows.filter((r) => Object.entries(r).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined));
    const punchList =
      selectedRowKeys.length > 0
        ? filteredRows
            .filter(
              (r) => selectedRowKeys.includes(r.key) && r.transactionStatus !== 'success', // Don't resubmit successful rows
            )
            .map(({ key, nameLocked, employeeIdInvalid, wbsIoInvalid, ...rest }) => ({
              ...rest,
              inDate: formatDateToYYYY(rest.inDate),
              outDate: formatDateToYYYY(rest.outDate),
              amount: rest.amount?.toString().replace(/^\$/, ''), // Remove $ prefix here
            }))
        : filteredRows
            .filter((r) => r.transactionStatus !== 'success')
            .map(({ key, nameLocked, employeeIdInvalid, wbsIoInvalid, ...rest }) => ({
              ...rest,
              inDate: formatDateToYYYY(rest.inDate),
              outDate: formatDateToYYYY(rest.outDate),
              amount: rest.amount?.toString().replace(/^\$/, ''), // Remove $ prefix here
            }));
    // Compulsory field validation
    const missingRequired = filteredRows.some((row) => requiredFields.some((field) => !row[field] || row[field].toString().trim() === ''));

    if (punchList.length === 0) {
      message.error('No valid entries to submit');
      return;
    }

    if (missingRequired) {
      if (activeTab === 'time') {
        message.error('For Time Entry, please fill all compulsory fields: Employee ID, Name, In Date, and In Time');
      } else if (activeTab === 'pay') {
        message.error('For Pay Code Edit, please fill all compulsory fields: Employee ID, Name, In Date, Pay Code, and Amount.');
      } else {
        message.error('Please fill all compulsory fields before switching tabs.');
      }
      return;
    }

    const payload = {
      batchId: batchId,
      batchType: activeTab,
      punchList,
    };
    await exportBatchToKronos(payload);
    setIsSentToKronos(true);
    message.success('Batch submitted to Kronos');
    setSelectedRowKeys([]); // Clear selection
    // Remove all empty rows from the UI after submit
    setRows((prevRows) => prevRows.filter((r) => Object.entries(r).some(([k, v]) => k !== 'key' && v !== '' && v !== null && v !== undefined)));
    // Call getBatch immediately after submit
    try {
      const { data } = await getBatch(batchId, false);
      const statusMap = data.batch?.submitToKronosStatus || {};
      // Update rows with the new status
      setRows((prev) =>
        prev.map((row) => {
          const employeeStatus = statusMap[row.employeeId];
          if (employeeStatus) {
            const matchingPunch = employeeStatus.find((punch) => punch.punchId === row._id);
            if (matchingPunch) {
              return {
                ...row,
                transactionStatus: matchingPunch.status,
                transactionError: matchingPunch.lastError || null,
              };
            }
          }
          return row;
        }),
      );
    } catch (err) {
      console.error('Error fetching batch status after submit', err);
    }
    // After immediate fetch, start polling every 5 seconds
    startPollingStatus(batchId);
  };

  useEffect(() => {
    const activeRef = activeTab === 'time' ? timeRef : payRef;
    if (activeRef.current) {
      setIndicatorStyle({ width: `${activeRef.current.offsetWidth}px`, transform: `translateX(${activeRef.current.offsetLeft}px)` });
    }
  }, [activeTab]);

  useEffect(() => {
    setEmployeeCount(rows.length);
  }, [rows]);

  useEffect(() => {
    getRolePermissionDetails();
    getBranchesHyperfind();
  }, []);

  const getRolePermissionDetails = () => {
    getDetails(userConfig.orgId)
      .then((response) => {
        const fetchRole = response.data.find(
          (role) =>
            role?.selectedRole?.toLowerCase().trim() === userConfig.role?.toLowerCase().trim() &&
            role?.selectedRoleType?.toLowerCase().trim() === userConfig.roleName?.selectedRoleType?.toLowerCase().trim(),
        );
        const zoomPermissions = fetchRole.permissions.cap.zoomEntry;
        setPermissions(zoomPermissions);

        // Set initial tab based on permissions
        const timeAccess = zoomPermissions.find((p) => p.feature === 'Time Entry')?.access;
        const payAccess = zoomPermissions.find((p) => p.feature === 'PayCode Entry')?.access;
        if (!timeAccess && payAccess) {
          setActiveTab('pay');
        } else {
          setActiveTab('time');
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getBranchesHyperfind = async () => {
    getBranches()
      .then((response) => {
        console.log(response);
        setBranches(response.data.branches || []);
      })
      .catch((err) => {
        setBranches([]);
        console.error(err);
      });
  };

  return (
    <div className="time-pay-table-root">
      <h2 style={{ margin: 6 }}>Zoom Time Entry</h2>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <BatchInfo
          activeTab={activeTab}
          rows={rows}
          setRows={setRows}
          employeeCount={employeeCount}
          setEmployeeCount={setEmployeeCount}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          batchId={batchId}
          setBatchId={setBatchId}
          batchSuggestions={batchSuggestions}
          setBatchSuggestions={setBatchSuggestions}
          originalBatchId={originalBatchId}
          setOriginalBatchId={setOriginalBatchId}
          isDirty={isDirty}
          setIsDirty={setIsDirty}
          startPollingStatus={startPollingStatus}
          stopPollingStatus={stopPollingStatus}
          setIsSentToKronos={setIsSentToKronos}
          isSentToKronos={isSentToKronos}
          anyInvalidRow={anyInvalidRow}
          selectedBranch={selectedBranch}
        />
        <div>
          <h3>Select Branch</h3>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select Branch"
            value={selectedBranch}
            onChange={(value) => setSelectedBranch(value)}
            options={branches.map((branch) => ({
              value: branch,
              label: branch,
            }))}
            allowClear
          />
        </div>
      </div>
      <div className="top-row">
        <div className="left-group">
          <div className="entries-heading">Entries</div>
          <div className="tabs-container">
            <div className="tab-buttons">
              <div className="tab-outline" style={indicatorStyle} />
              <div
                ref={timeRef}
                className={`tab-btn ${activeTab === 'time' ? 'active' : ''}`}
                onClick={() => {
                  if (getZoomPermission('time')?.access) {
                    handleTabChange('time');
                  } else {
                    message.error('You do not have permission to access Time Entry.');
                  }
                }}
                style={{
                  cursor: getZoomPermission('time')?.access ? 'pointer' : 'not-allowed',
                  opacity: getZoomPermission('time')?.access ? 1 : 0.5,
                  pointerEvents: getZoomPermission('time')?.access ? 'auto' : 'none',
                }}
              >
                Time entry
              </div>
              <div
                ref={payRef}
                className={`tab-btn ${activeTab === 'pay' ? 'active' : ''}`}
                onClick={() => {
                  if (getZoomPermission('pay')?.access) {
                    handleTabChange('pay');
                  } else {
                    message.error('You do not have permission to access Pay Code edit.');
                  }
                }}
                style={{
                  cursor: getZoomPermission('pay')?.access ? 'pointer' : 'not-allowed',
                  opacity: getZoomPermission('pay')?.access ? 1 : 0.5,
                  pointerEvents: getZoomPermission('pay')?.access ? 'auto' : 'none',
                }}
              >
                Pay Code edit
              </div>
            </div>
          </div>
        </div>
        <div className="right-buttons">
          <Button className="submit-btn" type="default" onClick={submitToKronos} disabled={!batchId || anyInvalidRow || allRowsSuccess || !zoomPerm?.access || batchId !== originalBatchId || isDirty || !selectedBranch}>
            Submit Batch to Kronos
          </Button>
          <Button className="submit-btn" type="default" onClick={generateAuditReport} disabled={!zoomPerm?.access || !selectedBranch}>
            Generate Pre-Submission Audit Report
          </Button>
          <Button className="icon-btn" onClick={() => deleteSelected()} danger disabled={!zoomPerm?.access || !selectedBranch}>
            <DeleteOutlined />
          </Button>
        </div>
      </div>
      <div className="table-wrap">
        <Table dataSource={rows} columns={activeTab === 'time' ? timeColumns : payColumns} pagination={false} rowSelection={rowSelection} sticky />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
          <Button onClick={addRows} disabled={(batchId === originalBatchId && isSentToKronos) || !zoomPerm?.access || !selectedBranch}>
            Add more rows
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TimeAndPayTable;
